import { Base64 } from 'js-base64';

import type { EdgeRequest } from '../next-use';

export class CacheKey {
    static readonly cacheKeyId = 'K';

    private search: string[] = [];
    private cookies: string[] = [];
    private headers: string[] = [];

    public addSearchParams(...sp: string[]) {
        this.search = [...new Set([...this.search, ...sp])];
        return this;
    }

    public addCookies(...cookies: string[]) {
        this.cookies = [...new Set([...this.cookies, ...cookies])];
        return this;
    }

    public addHeaders(...headers: string[]) {
        this.headers = [...new Set([...this.headers, ...headers])];
        return this;
    }

    public encode(req: EdgeRequest) {
        const search = this.search
            .filter(s => req.parsedUrl.searchParams.has(s))
            .sort((a, b) => a.localeCompare(b))
            .map(s => `${s}=${req.parsedUrl.searchParams.get(s)}`)
            .join('&');

        const headers = this.headers
            .filter(h => req.headers.has(h))
            .sort((a, b) => a.localeCompare(b))
            .map(h => `${h}=${req.headers.get(h)}`)
            .join('&');

        const cookies = this.cookies
            .filter(c => req.cookies.has(c))
            .sort((a, b) => a.localeCompare(b))
            .map(c => `${c}=${req.cookies.get(c)?.value}`)
            .join('&');

        const cacheKey = [
            CacheKey.cacheKeyId,
            search.length && `s:${search}`,
            headers.length && `h:${headers}`,
            cookies.length && `c:${cookies}`,
        ]
            .filter(Boolean)
            .join(';');

        return Base64.encodeURI(cacheKey);
    }

    static decode(key: string): Record<'search' | 'headers' | 'cookies', ReadonlyMap<string, string>> {
        const cacheKey = Base64.decode(key);
        const parts = cacheKey.split(';');
        const id = parts.shift();

        const result = {
            search: new Map<string, string>(),
            headers: new Map<string, string>(),
            cookies: new Map<string, string>(),
        };

        if (id !== CacheKey.cacheKeyId) {
            return result;
        }

        parts.forEach(part => {
            const type = part[0];
            const value = part.slice(2);

            const target = {
                s: result.search,
                h: result.headers,
                c: result.cookies,
            }[type];

            if (!target) return;

            value?.split('&').forEach(pair => {
                const [k, v] = pair.split('=');

                if (k && v) {
                    target.set(k, v);
                }
            });
        });

        return result;
    }

    static isValid(key: string) {
        if (!Base64.isValid(key)) return false;

        const cacheKey = Base64.decode(key);
        const id = cacheKey.split(';', 1).at(0) ?? '';
        if (id !== CacheKey.cacheKeyId) return false;

        try {
            const { cookies } = CacheKey.decode(key);
            const decisions = cookies.get('x-petm-decisions') ?? '{}';
            JSON.parse(decisions);

            return true;
        } catch {
            return false;
        }
    }
}
